import { TrackingEventType } from './events';
import { TrackingMetadata } from './metadata';

/**
 * Priority levels for tracking events
 * Lower numbers indicate higher priority
 */
export enum EventPriority {
  CRITICAL = -1,
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4,
}

/**
 * Base interface for all tracking events
 */
export interface TrackingEvent {
  /** The type of event */
  type: TrackingEventType;

  /** When the event occurred (timestamp) */
  timestamp: number;

  /** Priority level of the event */
  priority: EventPriority;

  /** Event-specific data */
  payload: unknown;
}

export type ErrorTrackingEvent = {
  type: 'Error';
  priority: EventPriority.ERROR;
  payload: { error: Error };
} & TrackingEvent;
export const isErrorTrackingEvent = (event: TrackingEvent): event is ErrorTrackingEvent => {
  return (
    typeof event === 'object' && 'type' in event && event.type === 'Error' && event.priority === EventPriority.ERROR
  );
};

/**
 * Returns a number indicating the sort order of two events based on their priority
 * @param eventA - The first event to compare
 * @param eventB - The second event to compare
 * @returns A negative number if eventA should come before eventB, a positive number
 * if eventA should come after eventB, or 0 if they are equal
 */
export function ordByPriority(eventA: TrackingEvent, eventB: TrackingEvent): number {
  return eventA.priority - eventB.priority;
}

/**
 * Returns a number indicating the sort order of two events based on their timestamp
 * @param eventA - The first event to compare
 * @param eventB - The second event to compare
 * @returns A negative number if eventA should come before eventB, a positive number
 * if eventA should come after eventB, or 0 if they are equal
 */
export function ordByTimestamp(eventA: TrackingEvent, eventB: TrackingEvent): number {
  return eventA.timestamp - eventB.timestamp;
}

/**
 * Returns a number indicating the sort order of two events based on their priority and timestamp
 * @param eventA - The first event to compare
 * @param eventB - The second event to compare
 * @returns A negative number if eventA should come before eventB, a positive number
 * if eventA should come after eventB, or 0 if they are equal
 */
export function ordByPriorityAndTimestamp(eventA: TrackingEvent, eventB: TrackingEvent): number {
  return ordByPriority(eventA, eventB) || ordByTimestamp(eventA, eventB);
}

export function serialize(event: TrackingEvent, metadata?: TrackingMetadata): string {
  return JSON.stringify({
    trackEvent: event.type,
    payload: sanitizeValue(event.payload),
    priority: EventPriority[event.priority] ?? String(event.priority),
    timestamp: event.timestamp,
    metadata: metadata ? sanitizeValue(metadata) : undefined,
  });
}

function sanitizeValue(value: unknown): unknown {
  if (value instanceof Error) {
    return {
      message: value.message,
      stack: value.stack,
      name: value.name,
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = sanitizeValue((value as Record<string, unknown>)[key]);
      return acc;
    }, {});
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return value;
}
